import { round4 } from '../catalog/catalog';

/**
 * Stock transfer, stock count and stock revaluation (piece 22, Stock transfer
 * and stock count; decisions 0122, 0195). 🔨 A physical count is a function
 * that corrects the on-hand number, not only a report that shows it, and
 * `expectedQuantity` is snapshotted when the line is created and never read
 * at posting. 🔨 A count is posted once and never edited; re-counting is a
 * new count.
 */
export const STOCK_COUNT_SCOPES = ['FullSite', 'ItemCategory', 'Spot'] as const;
export type StockCountScopeValue = (typeof STOCK_COUNT_SCOPES)[number];

/** A transfer moves stock from one site to another; the same site on both ends moves nothing. */
export function transferProblem(input: { fromSiteId?: string | null; toSiteId?: string | null; lineCount: number }): string | null {
  if (!input.fromSiteId) return 'a transfer names the site the stock leaves; choose where the stock is now';
  if (!input.toSiteId) return 'a transfer names the site the stock arrives at; choose where the stock is going';
  if (input.fromSiteId === input.toSiteId) return 'a transfer moves stock between two sites; these are the same site';
  if (input.lineCount < 1) return 'a transfer carries at least one line; add the items and quantities being moved';
  return null;
}

/** Draft despatches to InTransit, InTransit arrives at Received, and either cancels; a cancelled transfer in transit sends its goods back (decision 0980). */
export function transferMoveProblem(from: string, to: string): string | null {
  const allowed: Readonly<Record<string, readonly string[]>> = { Draft: ['InTransit', 'Cancelled'], InTransit: ['Received', 'Cancelled'], Received: [], Cancelled: [] };
  if (!(allowed[from] ?? []).includes(to)) return `a transfer at ${from} does not move to ${to}`;
  return null;
}

export interface CountLineFacts {
  id: string;
  itemId: string;
  /** Read from the snapshot taken when the line was created, never re-read at posting. */
  expectedQuantity: number;
  countedQuantity: number | null;
  varianceNote: string | null;
}

export interface CountVariance {
  lineId: string;
  itemId: string;
  /** Signed: positive is an overage coming in, negative a shortage going out. */
  quantityDelta: number;
  /** 🔨 Supplies the Adjustment movement's reasonNote, which is required on that reason. */
  reasonNote: string;
}

/**
 * 🔨 Posting writes one Adjustment movement per varying line and writes
 * nothing for a line that matches — a movement of zero is noise in an
 * append-only table. An uncounted line is not a variance either: nobody
 * looked at that shelf.
 */
export function countVariances(lines: readonly CountLineFacts[], countNumber: string): CountVariance[] {
  const out: CountVariance[] = [];
  for (const line of lines) {
    if (line.countedQuantity === null) continue;
    const quantityDelta = round4(line.countedQuantity - line.expectedQuantity);
    if (quantityDelta === 0) continue;
    out.push({ lineId: line.id, itemId: line.itemId, quantityDelta, reasonNote: line.varianceNote?.trim() || `Stock count ${countNumber}` });
  }
  return out;
}

/** A count is posted from Counting or Review, once; a posted count refuses every edit and a re-count is a new count. */
export function countPostProblem(status: string): string | null {
  if (status === 'Posted') return 'this count is posted; a posted count is never edited and a re-count is a new count';
  if (status === 'Cancelled') return 'this count was canceled';
  if (status === 'Draft') return 'a count is posted once it has been counted; move it to Counting first';
  return null;
}

/** Any write to a posted or cancelled count is refused, including a line's counted quantity. */
export function countEditProblem(status: string): string | null {
  if (status === 'Posted') return 'this count is posted; a posted count is never edited and a re-count is a new count';
  if (status === 'Cancelled') return 'this count was canceled and takes no further counting';
  return null;
}

export function countProblem(input: { siteId?: string | null; scope?: string | null }): string | null {
  if (!input.siteId) return 'a count names the site whose stock it counts; choose the site being counted';
  if (!input.scope || !(STOCK_COUNT_SCOPES as readonly string[]).includes(input.scope)) return `a count’s scope is one of ${STOCK_COUNT_SCOPES.join(', ')}`;
  return null;
}

/**
 * 🔨 A revaluation is an Accounting and Admin action and `reason` is required
 * (decision 0195). The quantity goes out at the old cost and back in at the
 * new one under one `reasonNote`, so the quantity on hand is unchanged and
 * only the value moves.
 */
export function revaluationProblem(input: { itemId?: string | null; siteId?: string | null; quantity?: number | null; oldUnitCost?: number | null; newUnitCost?: number | null; reason?: string | null }): string | null {
  if (!input.itemId) return 'a revaluation names the Item whose stock it reprices; choose the Item';
  if (!input.siteId) return 'a revaluation names the site the stock sits at; choose the site holding the stock';
  if (!input.quantity || !(input.quantity > 0)) return 'a revaluation reprices a quantity above zero';
  if (input.oldUnitCost === null || input.oldUnitCost === undefined || input.oldUnitCost < 0) return 'a revaluation carries the cost the stock is carried at; enter the cost it is valued at today';
  if (input.newUnitCost === null || input.newUnitCost === undefined || input.newUnitCost < 0) return 'a revaluation carries the cost the stock is being moved to; enter the cost it is being revalued to';
  if (input.oldUnitCost === input.newUnitCost) return 'a revaluation changes the value of stock on hand; these two costs are the same';
  if (!input.reason || !input.reason.trim()) return 'a revaluation states its reason; it is an Accounting and Admin action and the reason is required';
  return null;
}

/** The paired Adjustment movement a revaluation writes: out at the old cost, back in at the new one, under one note. */
export function revaluationPair(input: { quantity: number; oldUnitCost: number; newUnitCost: number; revaluationNumber: string; reason: string }): readonly { quantityDelta: number; unitCost: number; reasonNote: string }[] {
  const reasonNote = `${input.revaluationNumber}: ${input.reason.trim()}`;
  return [
    { quantityDelta: -round4(input.quantity), unitCost: input.oldUnitCost, reasonNote },
    { quantityDelta: round4(input.quantity), unitCost: input.newUnitCost, reasonNote },
  ];
}
